import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useMusicPlayer } from '../context/MusicPlayerContext';
import MusicAPI from '../services/musicApi';
import ReactPaginate from 'react-paginate';

// --- Enhanced Icons with better styling ---
const Icon = {
  Play: (p) => (<svg viewBox="0 0 24 24" fill="currentColor" className={p.className}><path d="M8 5v14l11-7z" /></svg>),
  Pause: (p) => (<svg viewBox="0 0 24 24" fill="currentColor" className={p.className}><path d="M6 5h4v14H6zM14 5h4v14h-4z" /></svg>),
  Heart: (p) => (<svg viewBox="0 0 24 24" fill="currentColor" className={p.className}><path d="M12.1 21.35l-1.1-.99C5.14 15.28 2 12.36 2 8.92 2 6.11 4.24 4 6.99 4c1.54 0 3.04.73 4.01 1.88A5.2 5.2 0 0115.01 4C17.76 4 20 6.11 20 8.92c0 3.44-3.14 6.36-8.99 11.44l-1.1.99z" /></svg>),
  HeartOutline: (p) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={p.className}><path d="M12.1 20.3C6.2 15.2 3 12.3 3 8.9 3 6.1 5.2 4 7.9 4c1.6 0 3.1.8 4.1 2.1C13 4.8 14.5 4 16.1 4 18.8 4 21 6.1 21 8.9c0 3.4-3.2 6.3-8.9 11.4l-.1.1-.1-.1z" /></svg>),
  Search: (p) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={p.className}><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>),
  Clock: (p) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={p.className}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 3" /></svg>),
  SkipNext: (p) => (<svg viewBox="0 0 24 24" fill="currentColor" className={p.className}><path d="M6 6l8.5 6L6 18V6zm9.5 0h2v12h-2V6z" /></svg>),
  SkipPrev: (p) => (<svg viewBox="0 0 24 24" fill="currentColor" className={p.className}><path d="M18 6v12l-8.5-6L18 6zM5.5 6h2v12h-2z" /></svg>),
  Volume: (p) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={p.className}><path d="M11 5l-5 4H3v6h3l5 4V5z" /><path d="M15 9a4 4 0 010 6" /><path d="M17 7a7 7 0 010 10" /></svg>),
  VolumeMute: (p) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={p.className}><path d="M11 5l-5 4H3v6h3l5 4V5z" /><path d="M21 9l-6 6M15 9l6 6" /></svg>),
  Home: (p) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={p.className}><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" /><path d="M9 22V12h6v10" /></svg>),
  Library: (p) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={p.className}><path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z" /></svg>),
  Plus: (p) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={p.className}><path d="M12 5v14M5 12h14" /></svg>),
  Globe: (p) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={p.className}><circle cx="12" cy="12" r="10" /><path d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" /></svg>),
  Shuffle: (p) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={p.className}><path d="M16 3h5v5M4 20L21 3M21 16v5h-5M15 15l6 6M4 4l5 5" /></svg>),
  Repeat: (p) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={p.className}><path d="M17 1l4 4-4 4M7 23l-4-4 4-4M14 5a9 9 0 019 9M3 10a9 9 0 019-9" /></svg>),
  RepeatOne: (p) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={p.className}><path d="M17 1l4 4-4 4M7 23l-4-4 4-4M14 5a9 9 0 019 9M3 10a9 9 0 019-9" /><path d="M11 12h1v4" /></svg>),
  List: (p) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={p.className}><path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" /></svg>),
}

// --- Utils ---
function mmss(sec) {
  const sInt = Math.max(0, Math.floor(sec || 0));
  const m = Math.floor(sInt / 60);
  const s = (sInt % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

function Cover({ hue = 200, url, className = '' }) {
  const [loaded, setLoaded] = useState(false);
  
  return url ? (
    <div className={`relative ${className}`}>
      {!loaded && (
        <div className="absolute inset-0 bg-gradient-to-br from-slate-700 to-slate-800 animate-pulse rounded-2xl" />
      )}
      <img 
        src={url} 
        alt="cover" 
        className={`w-full h-full object-cover rounded-2xl transition-all duration-500 ${loaded ? 'opacity-100 scale-100' : 'opacity-0 scale-105'} ${className}`}
        onLoad={() => setLoaded(true)}
      />
    </div>
  ) : (
    <div
      className={`relative aspect-square w-full rounded-2xl overflow-hidden ${className}`}
      style={{
        background: `linear-gradient(135deg, hsl(${hue} 80% 55%), hsl(${(hue + 60) % 360} 75% 45%))`,
      }}
    />
  )
}

// Enhanced Lazy Image Component
const LazyImage = ({ src, alt, className = '' }) => {
  const [loaded, setLoaded] = useState(false);
  
  return (
    <div className={`relative overflow-hidden ${className}`}>
      {!loaded && (
        <div className="absolute inset-0 bg-gradient-to-br from-slate-700 to-slate-800 animate-pulse rounded-2xl" />
      )}
      <img
        src={src}
        alt={alt}
        className={`w-full h-full object-cover transition-all duration-500 ${
          loaded ? 'opacity-100 scale-100' : 'opacity-0 scale-105'
        } ${className}`}
        onLoad={() => setLoaded(true)}
        loading="lazy"
      />
    </div>
  );
};

// Enhanced Favorites Component
const FavoritesView = ({ favorites, songs, onPlayTrack, currentTrack, isPlaying, onToggleFavorite, onBack }) => {
  const favoriteSongs = songs.filter(song => favorites.includes(song._id));

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-purple-950/20 to-slate-950">
      <div className="px-4 sm:px-6 lg:px-8 py-6">
        {/* Enhanced Header */}
        <div className="flex items-center gap-6 mb-8">
          <button
            onClick={onBack}
            className="p-3 hover:bg-white/10 rounded-2xl transition-all duration-300 backdrop-blur-sm border border-white/10 hover:border-white/20"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="h-20 w-20 rounded-2xl overflow-hidden flex-shrink-0 bg-gradient-to-br from-fuchsia-500 via-purple-600 to-blue-600 flex items-center justify-center shadow-2xl">
            <Icon.Heart className="w-10 h-10 text-white" />
          </div>
          <div className="flex-1">
            <h2 className="text-3xl font-bold bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent">
              Favorite Songs
            </h2>
            <p className="text-slate-300 mt-2">
              {favoriteSongs.length} {favoriteSongs.length === 1 ? 'song' : 'songs'} in your favorites
            </p>
          </div>
        </div>

        {/* Enhanced Favorites List */}
        {favoriteSongs.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-slate-700 to-slate-800 rounded-3xl flex items-center justify-center">
              <Icon.HeartOutline className="w-12 h-12 text-slate-400" />
            </div>
            <h3 className="text-2xl font-medium text-slate-300 mb-3">No favorites yet</h3>
            <p className="text-slate-400 max-w-md mx-auto">
              Start adding songs to your favorites by clicking the heart icon on any song
            </p>
          </div>
        ) : (
          <div className="bg-white/5 backdrop-blur-sm rounded-3xl border border-white/10 overflow-hidden shadow-2xl">
            <div className="p-6 bg-gradient-to-r from-white/5 to-transparent border-b border-white/10">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold">Your Favorites</h3>
                <span className="px-3 py-1 bg-fuchsia-500/20 text-fuchsia-300 rounded-full text-sm">
                  {favoriteSongs.length} songs
                </span>
              </div>
            </div>
            <div className="max-h-[60vh] overflow-y-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10 text-left text-slate-400 text-sm">
                    <th className="p-6 w-12 text-center">#</th>
                    <th className="p-6">Title</th>
                    <th className="p-6 hidden sm:table-cell">Artists</th>
                    <th className="p-6 w-20 text-center">❤️</th>
                    <th className="p-6 text-right w-20">
                      <Icon.Clock className="w-4 h-4 inline" />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {favoriteSongs.map((song, index) => (
                    <tr
                      key={song._id}
                      className={`border-b border-white/5 hover:bg-white/10 cursor-pointer transition-all duration-300 group ${
                        currentTrack?._id === song._id ? 'bg-gradient-to-r from-fuchsia-500/10 to-purple-500/10' : ''
                      }`}
                      onClick={() => onPlayTrack(song)}
                    >
                      <td className="p-6 w-12 text-center">
                        {currentTrack?._id === song._id && isPlaying ? (
                          <div className="w-4 h-4 flex items-center justify-center">
                            <div className="w-1 h-3 bg-fuchsia-500 mx-0.5 animate-pulse rounded-full"></div>
                            <div className="w-1 h-3 bg-fuchsia-500 mx-0.5 animate-pulse rounded-full" style={{animationDelay: '0.1s'}}></div>
                            <div className="w-1 h-3 bg-fuchsia-500 mx-0.5 animate-pulse rounded-full" style={{animationDelay: '0.2s'}}></div>
                          </div>
                        ) : (
                          <span className="text-slate-400 group-hover:text-white transition-colors">
                            {index + 1}
                          </span>
                        )}
                      </td>
                      <td className="p-6">
                        <div className="flex items-center gap-4">
                          <div className="h-12 w-12 flex-shrink-0 rounded-xl overflow-hidden shadow-lg">
                            <LazyImage
                              src={song.image}
                              alt={song.title}
                              className="h-full w-full group-hover:scale-110 transition-transform duration-300"
                            />
                          </div>
                          <div>
                            <div className={`font-semibold transition-colors ${
                              currentTrack?._id === song._id ? 'text-fuchsia-400' : 'text-white'
                            }`}>
                              {song.title}
                            </div>
                            <div className="text-sm text-slate-400 sm:hidden mt-1">
                              {song.artists?.join(', ')}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="p-6 hidden sm:table-cell text-slate-300 text-sm">
                        {song.artists?.join(', ')}
                      </td>
                      <td className="p-6 w-20 text-center">
                        <button
                          onClick={(e) => onToggleFavorite(song._id, e)}
                          className="p-2 hover:scale-110 transition-all duration-300 bg-fuchsia-500/10 rounded-xl hover:bg-fuchsia-500/20"
                        >
                          <Icon.Heart className="w-5 h-5 text-fuchsia-400" />
                        </button>
                      </td>
                      <td className="p-6 text-right text-slate-400 text-sm w-20">
                        {mmss(song.durationInSeconds)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Main Enhanced Component
export default function MusicLibrary() {
  const { playSong, currentSong, isPlaying, handleNext, handlePrevious, currentTime, duration } = useMusicPlayer();
  
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [subCategories, setSubCategories] = useState([]);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [view, setView] = useState('categories');
  const [searchQuery, setSearchQuery] = useState('');
  const [favorites, setFavorites] = useState([]);
  const [songsLoading, setSongsLoading] = useState(false);
  const [subCategoriesLoading, setSubCategoriesLoading] = useState(false);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalSongs, setTotalSongs] = useState(0);
  const songsPerPage = 9999;

  // Load favorites from localStorage
  useEffect(() => {
    const savedFavorites = localStorage.getItem('musicPlayer-favorites');
    if (savedFavorites) setFavorites(JSON.parse(savedFavorites));
  }, []);

  // Touch handlers for swipe navigation
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    touchEndX.current = e.changedTouches[0].clientX;
    const diff = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 50;
    
    if (Math.abs(diff) > minSwipeDistance) {
      if (diff > 0) {
        handleNext();
      } else {
        handlePrevious();
      }
    }
  };

  // Save favorites to localStorage
  useEffect(() => {
    localStorage.setItem('musicPlayer-favorites', JSON.stringify(favorites));
  }, [favorites]);

  // Fetch categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        setError('');

        const data = await MusicAPI.getCategories();
        if (data.success) {
          const customCategories = [
            { 
              _id: 'all', 
              name: 'All Songs', 
              description: 'All available songs',
              image: null 
            },
            { 
              _id: 'favorites', 
              name: 'Favorites', 
              description: 'Your favorite songs',
              image: null 
            },
            ...data.data.data
          ];
          setCategories(customCategories);
        } else {
          setError(data.message || 'Failed to load categories.');
        }
      } catch (err) {
        console.error("Error fetching categories:", err);
        setError('Failed to load categories. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  // Fetch subcategories for a category
  const fetchSubCategories = async (category) => {
    try {
      setSubCategoriesLoading(true);
      setError('');

      const data = await MusicAPI.getSubCategories(category._id);
      
      if (data.success) {
        const subCategoriesData = data.data.data || [];
        setSubCategories(subCategoriesData);
        setSelectedCategory(category);
        setView('subcategories');
      } else {
        setError(data.message || 'Failed to load subcategories');
      }
    } catch (err) {
      console.error("Error fetching subcategories:", err);
      setError('Failed to load subcategories. Please try again later.');
    } finally {
      setSubCategoriesLoading(false);
    }
  };

  // Fetch songs based on selection
  const fetchSongs = async (category = null, subCategory = null) => {
    try {
      setSongsLoading(true);
      setError('');

      let data;

      if (subCategory) {
        data = await MusicAPI.getSongsBySubCategory(subCategory._id);
        setSelectedSubCategory(subCategory);
      } else if (category?._id === 'all' || !category) {
        data = await MusicAPI.getAllSongs(currentPage, songsPerPage);
        setSelectedSubCategory(null);
        if (data.success && data.data) {
          setTotalSongs(data.data.totalCount || data.data.total || 0);
          setTotalPages(Math.ceil((data.data.totalCount || data.data.total || 0) / songsPerPage));
        }
      } else if (category?._id === 'favorites') {
        setSelectedCategory(category);
        setSelectedSubCategory(null);
        setView('favorites');
        setSongsLoading(false);
        return;
      } else {
        // For regular categories, check if they have subcategories
        await fetchSubCategories(category);
        setSongsLoading(false);
        return;
      }

      if (data.success) {
        const songsData = data.data.data || [];
        setSongs(songsData);
        setSelectedCategory(category);
        setView('songs');
      } else {
        setError(data.message || 'Failed to load songs');
      }
    } catch (err) {
      console.error("Error fetching songs:", err);
      setError('Failed to load songs. Please try again later.');
    } finally {
      setSongsLoading(false);
    }
  };

  // Filter songs based on search and favorites
  const filteredSongs = useMemo(() => {
    let filtered = songs;
    
    if (searchQuery) {
      filtered = filtered.filter(song => 
        song.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        song.artists?.some(artist => 
          artist.toLowerCase().includes(searchQuery.toLowerCase())
        )
      );
    }
    
    return filtered;
  }, [songs, searchQuery]);

  // Handle song click - using centralized music player
  const handleSongClick = (song) => {
    try {
      // Format song data to match expected structure
      const formattedSong = {
        id: song._id || song.id,
        title: song.title || song.name || 'Unknown Song',
        artist: song.artists?.join(', ') || song.artist || 'Unknown Artist',
        album: song.album || 'Unknown Album',
        cover: song.image || '/default-cover.jpg',
        audioUrl: song.audio || song.audioUrl || song.url,
        duration: mmss(song.durationInSeconds) || '3:20',
        plays: song.playCount || song.plays || 0,
        category: song.category || 'General'
      };

      // Use the current songs list as playlist
      const formattedList = songs.map(s => ({
        id: s._id || s.id,
        title: s.title || s.name || 'Unknown Song',
        artist: s.artists?.join(', ') || s.artist || 'Unknown Artist',
        album: s.album || 'Unknown Album',
        cover: s.image || '/default-cover.jpg',
        audioUrl: s.audio || s.audioUrl || s.url,
        duration: mmss(s.durationInSeconds) || '3:20',
        plays: s.playCount || s.plays || 0,
        category: s.category || 'General'
      }));

      // Play the song using centralized music player
      playSong(formattedSong, formattedList);
      console.log('Now playing:', formattedSong.title);
    } catch (error) {
      console.error('Error playing song:', error);
      setError('Failed to play song');
    }
  };

  const toggleFavorite = (songId, e) => {
    if (e) e.stopPropagation();
    setFavorites(prev => 
      prev.includes(songId) 
        ? prev.filter(id => id !== songId)
        : [...prev, songId]
    );
  };

  const navigateBack = () => {
    if (view === 'songs' || view === 'favorites') {
      if (selectedSubCategory) {
        setView('subcategories');
        setSelectedSubCategory(null);
        setSearchQuery('');
      } else {
        setView('categories');
        setSelectedCategory(null);
        setSearchQuery('');
      }
    } else if (view === 'subcategories') {
      setView('categories');
      setSelectedCategory(null);
      setSubCategories([]);
    }
  };

  const handleCategorySelect = (category) => {
    setCurrentPage(1);
    fetchSongs(category);
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchSongs(selectedCategory, selectedSubCategory);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const showFavorites = () => {
    setView('favorites');
    setSelectedCategory(categories.find(cat => cat._id === 'favorites'));
  };

  return (
    <div 
      className="min-h-screen w-full bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-slate-100"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {currentSong && (
        <div className="w-full h-1 bg-gray-600 rounded-full cursor-pointer">
          <div
            className="h-full bg-gradient-to-r from-green-500 to-green-400 rounded-full transition-all duration-150"
            style={{ width: `${Math.min(100, (currentTime / (duration || 1)) * 100)}%` }}
          />
        </div>
      )}
      {/* Enhanced Header */}
      <header className="border-b border-white/10 p-4 backdrop-blur-sm bg-slate-950/80 sticky top-0 z-30">
        <div className="mx-auto max-w-7xl flex items-center justify-between">
          <div className="flex items-center gap-4">
            {(view === 'songs' || view === 'favorites' || view === 'subcategories') && (
              <button
                onClick={navigateBack}
                className="p-3 hover:bg-white/10 rounded-2xl transition-all duration-300 backdrop-blur-sm border border-white/10 hover:border-white/20"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            <h1 className="text-2xl font-bold bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent">
              {view === 'subcategories' ? selectedCategory?.name : 'Music Library'}
            </h1>
          </div>

          {/* Enhanced Search Bar */}
          {(view === 'songs' || view === 'favorites') && (
            <div className="flex-1 max-w-md mx-4">
              <div className="relative">
                <Icon.Search className="w-5 h-5 absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search songs..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-white/10 border border-white/10 rounded-2xl py-3 pl-12 pr-6 text-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-fuchsia-500 focus:border-transparent backdrop-blur-sm transition-all duration-300"
                />
              </div>
            </div>
          )}

          <div className="flex items-center gap-2">
            <button
              onClick={showFavorites}
              className={`p-3 rounded-2xl transition-all duration-300 backdrop-blur-sm border ${
                view === 'favorites' 
                  ? 'text-fuchsia-500 border-fuchsia-500/30 bg-fuchsia-500/10' 
                  : 'text-slate-400 border-white/10 hover:text-white hover:border-white/20 hover:bg-white/5'
              }`}
            >
              <Icon.Heart className="w-6 h-6" />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="min-h-screen">
        {view === 'favorites' ? (
          <FavoritesView
            favorites={favorites}
            songs={songs}
            onPlayTrack={handleSongClick}
            currentTrack={currentSong}
            isPlaying={isPlaying}
            onToggleFavorite={toggleFavorite}
            onBack={navigateBack}
          />
        ) : (
          <div className="px-4 sm:px-6 lg:px-8 py-6">
            {error && (
              <div className="mb-6 p-4 bg-red-900/30 border border-red-700/50 rounded-2xl text-red-200 backdrop-blur-sm">
                {error}
              </div>
            )}

            {loading ? (
              <div className="flex justify-center items-center py-20">
                <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-fuchsia-500"></div>
              </div>
            ) : view === 'subcategories' ? (
              <div>
                <div className="flex items-center gap-6 mb-8">
                  <div className="h-20 w-20 rounded-2xl overflow-hidden flex-shrink-0 shadow-2xl">
                    {selectedCategory?.image ? (
                      <LazyImage
                        src={selectedCategory.image}
                        alt={selectedCategory.name}
                        className="h-full w-full"
                      />
                    ) : (
                      <Cover 
                        hue={Math.floor(Math.random() * 360)} 
                        className="h-full w-full"
                      />
                    )}
                  </div>
                  <div>
                    <h2 className="text-3xl capitalize font-bold bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent">
                      {selectedCategory?.name}
                    </h2>
                    <p className="text-slate-300 mt-2">
                      {subCategoriesLoading ? (
                        <span className="inline-flex items-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-fuchsia-500 mr-3"></div>
                          Loading subcategories...
                        </span>
                      ) : (
                        `${subCategories.length} ${subCategories.length === 1 ? 'subcategory' : 'subcategories'} available`
                      )}
                    </p>
                  </div>
                </div>

                {subCategoriesLoading ? (
                  <div className="flex justify-center items-center py-20">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-fuchsia-500"></div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {subCategories.map((subCategory) => (
                      <div
                        key={subCategory._id}
                        className="group cursor-pointer rounded-3xl overflow-hidden bg-white/5 backdrop-blur-sm border border-white/10 hover:border-fuchsia-500/40 transition-all duration-500 shadow-lg hover:shadow-2xl hover:scale-105"
                        onClick={() => fetchSongs(selectedCategory, subCategory)}
                      >
                        <div className="p-6">
                          <div className="relative aspect-square mb-4 rounded-2xl overflow-hidden">
                            {subCategory.image ? (
                              <LazyImage
                                src={subCategory.image}
                                alt={subCategory.name}
                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                              />
                            ) : (
                              <Cover 
                                hue={Math.floor(Math.random() * 360)} 
                                className="w-full h-full"
                              />
                            )}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                          </div>
                          <h3 className="text-lg font-semibold mb-2 capitalize truncate text-white">
                            {subCategory.name}
                          </h3>
                          <p className="text-sm capitalize text-slate-400 line-clamp-2">
                            {subCategory.description}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : view === 'categories' ? (
              <div>
                <h2 className="text-3xl font-bold mb-8 bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent">
                  Browse Categories
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {categories.map((category) => (
                    <div
                      key={category._id}
                      className="group cursor-pointer rounded-3xl overflow-hidden bg-white/5 backdrop-blur-sm border border-white/10 hover:border-fuchsia-500/40 transition-all duration-500 shadow-lg hover:shadow-2xl hover:scale-105"
                      onClick={() => handleCategorySelect(category)}
                    >
                      <div className="p-6">
                        <div className="relative aspect-square mb-4 rounded-2xl overflow-hidden">
                          {category.image ? (
                            <LazyImage
                              src={category.image}
                              alt={category.name}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                            />
                          ) : (
                            <Cover 
                              hue={Math.floor(Math.random() * 360)} 
                              className="w-full h-full"
                            />
                          )}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        </div>
                        <h3 className="text-lg capitalize font-semibold mb-2 truncate text-white">
                          {category.name}
                        </h3>
                        <p className="text-sm capitalize text-slate-400 line-clamp-2">
                          {category.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div>
                {/* Enhanced Category Header */}
                <div className="flex items-center gap-6 mb-8">
                  <div className="h-20 w-20 rounded-2xl overflow-hidden flex-shrink-0 shadow-2xl">
                    {selectedCategory?.image ? (
                      <LazyImage
                        src={selectedCategory.image}
                        alt={selectedCategory.name}
                        className="h-full w-full"
                      />
                    ) : (
                      <Cover 
                        hue={Math.floor(Math.random() * 360)} 
                        className="h-full w-full"
                      />
                    )}
                  </div>
                  <div>
                    <h2 className="text-3xl capitalize font-bold bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent">
                      {selectedSubCategory ? selectedSubCategory.name : (selectedCategory?.name || 'Songs')}
                    </h2>
                    <p className="text-slate-300 mt-2">
                      {selectedSubCategory && (
                        <span className="text-sm capitalize text-slate-400">
                          from {selectedCategory?.name} • 
                        </span>
                      )}
                      {songsLoading ? (
                        <span className="inline-flex items-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-fuchsia-500 mr-3"></div>
                          Loading songs...
                        </span>
                      ) : (
                        ` ${filteredSongs.length} ${filteredSongs.length === 1 ? 'song' : 'songs'} available${searchQuery ? ` for "${searchQuery}"` : ''}`
                      )}
                    </p>
                  </div>
                </div>

                {/* Enhanced Songs List */}
                {songsLoading ? (
                  <div className="flex justify-center items-center py-20">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-fuchsia-500"></div>
                  </div>
                ) : (
                  <div className="bg-white/5 backdrop-blur-sm rounded-3xl border border-white/10 overflow-hidden shadow-2xl">
                    <div className="p-6 bg-gradient-to-r from-white/5 to-transparent border-b border-white/10">
                      <div className="flex items-center justify-between">
                        <h3 className="text-xl font-semibold">Songs</h3>
                        <span className="px-3 py-1 bg-fuchsia-500/20 text-fuchsia-300 rounded-full text-sm">
                          {filteredSongs.length} songs
                        </span>
                      </div>
                    </div>
                    <div className="max-h-[60vh] overflow-y-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-white/10 text-left text-slate-400 text-sm">
                            <th className="p-6 w-12 text-center">#</th>
                            <th className="p-6">Title</th>
                            <th className="p-6 hidden sm:table-cell">Artists</th>
                            <th className="p-6 w-20 text-center">❤️</th>
                            <th className="p-6 text-right w-20">
                              <Icon.Clock className="w-4 h-4 inline" />
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredSongs.length === 0 ? (
                            <tr>
                              <td colSpan="5" className="p-12 text-center text-slate-400">
                                <div className="flex flex-col items-center">
                                  <Icon.Search className="w-12 h-12 text-slate-600 mb-4" />
                                  <p className="text-lg mb-2">No songs found</p>
                                  <p className="text-sm">
                                    {searchQuery ? 'Try adjusting your search terms' : 'No songs available in this category'}
                                  </p>
                                </div>
                              </td>
                            </tr>
                          ) : (
                            filteredSongs.map((song, index) => (
                              <tr
                                key={song._id}
                                className={`border-b border-white/5 hover:bg-white/10 cursor-pointer transition-all duration-300 group ${
                                  currentSong?.id === song._id ? 'bg-gradient-to-r from-fuchsia-500/10 to-purple-500/10' : ''
                                }`}
                                onClick={() => handleSongClick(song)}
                              >
                                <td className="p-6 w-12 text-center">
                                  {currentSong?.id === song._id && isPlaying ? (
                                    <div className="w-4 h-4 flex items-center justify-center">
                                      <div className="w-1 h-3 bg-fuchsia-500 mx-0.5 animate-pulse rounded-full"></div>
                                      <div className="w-1 h-3 bg-fuchsia-500 mx-0.5 animate-pulse rounded-full" style={{animationDelay: '0.1s'}}></div>
                                      <div className="w-1 h-3 bg-fuchsia-500 mx-0.5 animate-pulse rounded-full" style={{animationDelay: '0.2s'}}></div>
                                    </div>
                                  ) : (
                                    <span className="text-slate-400 group-hover:text-white transition-colors">
                                      {index + 1}
                                    </span>
                                  )}
                                </td>
                                <td className="p-6">
                                  <div className="flex items-center gap-4">
                                    <div className="h-12 w-12 flex-shrink-0 rounded-xl overflow-hidden shadow-lg">
                                      <LazyImage
                                        src={song.image}
                                        alt={song.title}
                                        className="h-full w-full group-hover:scale-110 transition-transform duration-300"
                                      />
                                    </div>
                                    <div>
                                      <div className={`font-semibold capitalize transition-colors ${
                                        currentSong?.id === song._id ? 'text-fuchsia-400' : 'text-white'
                                      }`}>
                                        {song.title}
                                      </div>
                                      <div className="text-sm text-slate-400 capitalize sm:hidden mt-1">
                                        {song.artists?.join(', ')}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td className="p-6 hidden sm:table-cell capitalize text-slate-300 text-sm">
                                  {song.artists?.join(', ')}
                                </td>
                                <td className="p-6 w-20 text-center">
                                  <button
                                    onClick={(e) => toggleFavorite(song._id, e)}
                                    className="p-2 hover:scale-110 transition-all duration-300 bg-white/5 rounded-xl hover:bg-fuchsia-500/10"
                                  >
                                    {favorites.includes(song._id) ? (
                                      <Icon.Heart className="w-5 h-5 text-fuchsia-400" />
                                    ) : (
                                      <Icon.HeartOutline className="w-5 h-5 text-slate-400 hover:text-fuchsia-400" />
                                    )}
                                  </button>
                                </td>
                                <td className="p-6 text-right text-slate-400 text-sm w-20">
                                  {mmss(song.durationInSeconds)}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                    
                    {/* Pagination with react-paginate */}
                    {totalPages > 1 && (
                      <div className="p-6 border-t border-white/10 flex flex-col items-center gap-4">
                        <ReactPaginate
                          previousLabel={'← Previous'}
                          nextLabel={'Next →'}
                          breakLabel={'...'}
                          pageCount={totalPages}
                          marginPagesDisplayed={2}
                          pageRangeDisplayed={3}
                          onPageChange={(event) => handlePageChange(event.selected + 1)}
                          forcePage={currentPage - 1}
                          containerClassName={'flex items-center gap-2'}
                          pageClassName={''}
                          pageLinkClassName={'px-4 py-2 bg-white/10 hover:bg-white/20 rounded-xl transition-all duration-300 text-sm'}
                          activeClassName={''}
                          activeLinkClassName={'!bg-fuchsia-500 text-white'}
                          previousClassName={''}
                          previousLinkClassName={'px-4 py-2 bg-white/10 hover:bg-white/20 rounded-xl transition-all duration-300 text-sm disabled:opacity-50'}
                          nextClassName={''}
                          nextLinkClassName={'px-4 py-2 bg-white/10 hover:bg-white/20 rounded-xl transition-all duration-300 text-sm disabled:opacity-50'}
                          breakClassName={''}
                          breakLinkClassName={'px-4 py-2 text-slate-400'}
                          disabledClassName={'opacity-50 cursor-not-allowed'}
                        />
                        <span className="text-sm text-slate-400">
                          Page {currentPage} of {totalPages} ({totalSongs} songs)
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}